<template>
  <div class="event-dates">
    <div class="event-dates__header">
      <DynamicHeading :level="3">
        {{ $t('DATES') }}
      </DynamicHeading>

      <BaseSegmentedButtons
        :options="[
          {
            display: $t('ALL'),
            value: 'all',
          },
          {
            display: $t('UPCOMING'),
            value: 'upcoming',
          },
          {
            display: $t('PAST'),
            value: 'past',
          },
        ]"
        @onSetActiveButton="handleChangeTab"
      />

      <router-link
        :to="{ name: ROUTE_EVENT_DATE_FORM, params: { courseId: 0 } }"
      >
        <BaseButton
          :title="$t('product_events.ADD_NEW_DATE')"
          :aria-label="$t('product_events.ADD_NEW_DATE')"
          theme="blue"
          size="small"
        >
          <template #icon><PlusIcon /></template
          >{{ $t('product_events.ADD_DATE') }}
        </BaseButton>
      </router-link>
    </div>

    <DialogModal
      v-if="isCancelModalOpen"
      :title="$t('product_events.CANCEL_DATE')"
      :description="$t('product_events.CANCEL_CONFIRM_TITLE')"
      :cta-button-text="$t('product_events.CANCEL_DATE')"
      :cancel-button-text="$t('BACK')"
      cta-button-theme="red"
      @onClose="closeCancelModal"
      @onConfirm="handleCancelEventDate"
    />

    <EventDatesTable
      :items="event.courses"
      @rowClicked="
        (eventDate) =>
          $router.replace({
            name: ROUTE_EVENT_DATE,
            params: { courseId: eventDate.id },
          })
      "
      @onClickCancelButton="
        (courseId) => {
          selectedCourseId = courseId
          openCancelModalDialog()
        }
      "
    />
  </div>
</template>

<script setup lang="ts">
import {
  ROUTE_EVENT_DATE,
  ROUTE_EVENT_DATE_FORM,
} from '@/router/routes/routeNames'
import EventDatesTable from '@/components/tables/EventDatesTable.vue'
import BaseButton from '@/components/generics/base/BaseButton.vue'
import DynamicHeading from '@/components/layout/DynamicHeading.vue'
import BaseSegmentedButtons from '@/components/generics/base/BaseSegmentedButtons.vue'
import PlusIcon from '@/components/icon_components/PlusIcon.vue'
import {
  type EventStatusFilter,
  useEventQuery,
} from '@/queries/events/eventQueries.ts'
import { ref } from 'vue'
import { useRoute } from 'vue-router/composables'
import DialogModal from '@/components/generics/modals/DialogModal.vue'
import { useQueryClient } from '@tanstack/vue-query'
import { useToast } from '@/hooks/useToast.ts'
import { useDialog } from '@/hooks/useDialog.ts'
import { updateProductEvent } from '@/service/v3/endpoints/events.ts'
import { QUERY_KEY_EVENT } from '@/queries/events/eventKeys.ts'
import { useI18n } from '@/hooks/useI18N.ts'

const i18n = useI18n()
const route = useRoute()
const { doToast } = useToast()
const queryClient = useQueryClient()
const selectedCourseId = ref(undefined)
const productId = route.params.productId
const currentDateFilter = ref<EventStatusFilter>('all') // TODO: Read from URL - !!NOTE!! Base segmented button does not know about the current active - it will just default to the first one

const handleChangeTab = ({ value }: { value: EventStatusFilter }) => {
  currentDateFilter.value = value
}

const { data: event } = useEventQuery(Number(productId), currentDateFilter)

const {
  isOpen: isCancelModalOpen,
  openDialog: openCancelModalDialog,
  closeDialog: closeCancelModal,
} = useDialog()

const handleCancelEventDate = async () => {
  const response = await updateProductEvent(productId, selectedCourseId.value, {
    cancelled: true,
  })

  if (response.error) {
    doToast(
      { text: i18n.t('product_events.toast_messages.CANCEL_DATE_ERROR') }
    )
  } else {
    closeCancelModal()
    await queryClient.invalidateQueries({
      queryKey: [QUERY_KEY_EVENT, Number(productId)],
    })
  }
}
</script>

<style lang="scss" scoped>
.event-dates {
  display: flex;
  flex-direction: column;
  flex: 1 1 0;

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: $spacing-xs;
    padding-bottom: $spacing-4xs;
  }
}

.no-dates-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: $spacing-4xs;

  &__text {
    @include subtitle;
    color: $color-text-light;
    text-align: center;
    padding: 0 $spacing-xs;
  }
}
</style>
